import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Store, MapPin, Clock, Tag } from 'lucide-react';

export default function VendorProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/vendor/profile');
        // Handle both formats: direct or wrapped in {vendor: {}}
        setProfile(res.data.vendor || res.data);
      } catch { toast.error('Failed'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: profile.name,
        description: profile.description,
        phone: profile.business_phone || profile.phone,
        address: profile.address,
        business_type: profile.business_type || 'restaurant',
        delivery_radius: profile.delivery_radius_miles || profile.delivery_radius,
        delivery_time: profile.delivery_time,
        is_open: profile.is_open
      };
      const res = await api.put('/vendor/profile', updateData);
      setProfile(res.data.vendor || res.data);
      toast.success('Profile saved');
      // Reload page to update business type context
      window.location.reload();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const update = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Store Profile</h1>
        <p className="text-muted-foreground mt-1">Update your store information and settings.</p>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-medium flex items-center gap-2"><Store className="h-4 w-4" /> Business Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2"><Label>Store Name</Label><Input value={profile?.name || ''} onChange={e => update('name', e.target.value)} data-testid="vendor-profile-name" /></div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Tag className="h-4 w-4" /> Business Type</Label>
              <Select value={profile?.business_type || 'restaurant'} onValueChange={v => update('business_type', v)}>
                <SelectTrigger data-testid="vendor-profile-business-type">
                  <SelectValue placeholder="Select business type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="restaurant">🍽️ Restaurant</SelectItem>
                  <SelectItem value="grocery">🛒 Grocery Store</SelectItem>
                  <SelectItem value="service">💼 Service Provider</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                This affects how your catalog and orders are displayed
              </p>
            </div>
            <div className="space-y-2"><Label>Phone</Label><Input value={profile?.business_phone || profile?.phone || ''} onChange={e => update('business_phone', e.target.value)} data-testid="vendor-profile-phone" /></div>
            <div className="md:col-span-2 space-y-2"><Label>Description</Label><Textarea value={profile?.description || ''} onChange={e => update('description', e.target.value)} rows={3} /></div>
            <div className="space-y-2"><Label>Address</Label><Input value={profile?.address || ''} onChange={e => update('address', e.target.value)} /></div>
            <div className="space-y-2"><Label>Delivery Time</Label><Input value={profile?.delivery_time || ''} onChange={e => update('delivery_time', e.target.value)} placeholder="25-35 min" /></div>
            <div className="space-y-2"><Label>Delivery Radius (miles)</Label><Input type="number" value={profile?.delivery_radius_miles || profile?.delivery_radius || ''} onChange={e => update('delivery_radius_miles', Number(e.target.value))} /></div>
            <div className="space-y-2">
              <Label>Store Status</Label>
              <div className="flex items-center gap-3 mt-2">
                <Switch checked={profile?.is_open || false} onCheckedChange={v => update('is_open', v)} data-testid="vendor-profile-open-switch" />
                <span className={`text-sm font-medium ${profile?.is_open ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                  {profile?.is_open ? 'Open for Orders' : 'Closed'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-8 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} data-testid="vendor-profile-save">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Service Hours */}
      {profile?.service_hours && (
        <Card>
          <CardHeader className="pb-4"><CardTitle className="text-base font-medium flex items-center gap-2"><Clock className="h-4 w-4" /> Service Hours</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(profile.service_hours).map(([day, hours]) => (
                <div key={day} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm font-medium capitalize">{day}</span>
                  <span className="text-sm text-muted-foreground">{hours}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Store stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><Store className="h-5 w-5 text-amber-600" /></div>
            <div><p className="text-xs text-muted-foreground">Category</p><p className="font-medium capitalize">{profile?.category}</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><MapPin className="h-5 w-5 text-blue-600" /></div>
            <div><p className="text-xs text-muted-foreground">Commission Rate</p><p className="font-medium">{((profile?.commission_rate || 0) * 100).toFixed(0)}%</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Clock className="h-5 w-5 text-emerald-600" /></div>
            <div><p className="text-xs text-muted-foreground">Account Status</p><p className="font-medium capitalize">{profile?.status}</p></div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
